// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { saveLoadFactory, SaveLocation } from 'application-storage';
import { call, put, takeLatest } from 'redux-saga/effects';

import CommonDal from './../../../dal/CommonDal';
import { logException } from './../../../lib/ApplicationInsights';
import CorrelationVector from './../../../lib/CorrelationVector';
import {
    actionStatusStarted,
    actionStatusFailed,
    actionStatusSuccess,
} from './../../../store/actionStatus';
import {
    PUT_SELECTED_COUNTRY_DATA,
    getCountriesData,
    getCountriesDataFail,
    getCountriesDataSuccess,
    putSelectedCountryDataSuccess,
} from './../../../store/countries';

const appStorage = saveLoadFactory('CountriesList', SaveLocation.LocalStorage);

export function* fetchListOfCountries(): IterableIterator<any> {
    try {
        if (appStorage.getValue()) {
            yield put(getCountriesDataSuccess(appStorage.getValue()));
        }

        const response: any | any[] = yield call(CommonDal.getCountriesList);

        if (response && Array.isArray(response) && response.length) {
            appStorage.setValue(response);
            yield put(getCountriesDataSuccess(response));
        } else {
            const error = new Error('Unexpected response from getCountries (expected non-zero length array)');
            // Manually log the exception to make it easier to find
            logException(CorrelationVector.getCurrentValue(), error, {
                response: JSON.stringify(response),
                responseIsUndefined: response === undefined,
            });
            yield put(getCountriesDataFail(error));
        }
    } catch (e) {
        yield put(getCountriesDataFail(e));
    }
}

export function* putSelectedCountry(action: any): IterableIterator<any> {
    try {
        yield put(
            actionStatusStarted({
                actionName: PUT_SELECTED_COUNTRY_DATA,
            })
        );

        yield put(putSelectedCountryDataSuccess(action.payload));

        yield put(
            actionStatusSuccess({
                actionName: PUT_SELECTED_COUNTRY_DATA,
            })
        );
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: PUT_SELECTED_COUNTRY_DATA,
                error: e,
            })
        );
    }
}

export default [
    takeLatest(PUT_SELECTED_COUNTRY_DATA, putSelectedCountry),
    takeLatest(`${getCountriesData}`, fetchListOfCountries),
];
